<template lang="html">
  <div class="activity">
    <DitActivityGraph v-if="mobile" :activity-data="$store.state.application.weeklyActivity" />
    <div class="card">
      <template v-if="groups.length > 0">
        <div v-for="group in groups" class="group" :key="group.key">
          <h2>{{$options.filters.date(group.key, '{dd} {Mon} {year}')}}</h2>
          <div v-for="(entry, index) in group.entries" class="entry" :key="index">
            <div class="task">
              <p>{{entry.title}}</p>
              <span v-if="entry.project">{{entry.project}}</span>
            </div>
            <span class="hours">{{entry.hours}}h</span>
          </div>
        </div>
      </template>
      <div v-else class="group empty-state">
        <h2>There’s no activity yet.</h2>
        <p>Track some time on a task to have it appear here.</p>
      </div>
    </div>
    <IntersectionGuard v-if="initialized && nextPageAvailable" :root="this.$el" root-margin="0px 0px 100px 0px" @enter="fetchTasksPage">
      <DitButton class="load-more-button" @click="fetchTasksPage">Load More</DitButton>
    </IntersectionGuard>
  </div>
</template>

<script>
import date from '@/filters/date';
import db from '@/db';

import DitActivityGraph from '@/components/DitActivityGraph.vue';
import DitButton from '@/components/DitButton.vue';
import IntersectionGuard from '@/components/IntersectionGuard.vue';

export default {
  components: {
    DitActivityGraph,
    DitButton,
    IntersectionGuard,
  },
  computed: {
    mobile() {
      return this.$store.state.application.mobile;
    },
  },
  created() {
    this.$store.dispatch('loadWeeklyActivity');
    this.fetchTasksPage();
  },
  data() {
    return {
      currentPage: 0,
      groups: [],
      initialized: false,
      nextPageAvailable: true,
      pageSize: 5,
      totalTasks: -1,
    };
  },
  filters: {
    date,
  },
  methods: {
    async fetchTasksPage() {
      // using offset pagination since lastActivity is not unique
      if (this.totalTasks === -1) this.totalTasks = await db.tasks.orderBy('lastActivity').count();

      const tasks = await db.tasks
        .orderBy('lastActivity')
        .reverse()
        .offset(this.pageSize * this.currentPage)
        .limit(this.pageSize)
        .toArray();

      this.currentPage += 1;
      this.nextPageAvailable = this.currentPage < Math.ceil(this.totalTasks / this.pageSize);

      tasks.forEach(({ project, title, trackedTime }) => {
        Object.entries(trackedTime).forEach(([timestamp, hours]) => {
          if (hours > 0) {
            // const formattedDate = this.$options.filters.date(Number.parseInt(timestamp, 10), '{dd} {Mon} {year}');
            const normalizedTimestamp = new Date(Number.parseInt(timestamp, 10)).setHours(0, 0, 0, 0).valueOf();
            const groupIndex = this.groups.findIndex((group) => group.key === normalizedTimestamp);
            let actualGroupIndex = groupIndex;

            if (groupIndex < 0) {
              const nextIndex = this.groups.findIndex((group) => group.key < normalizedTimestamp);
              if (nextIndex < 0) {
                this.groups.push({ key: normalizedTimestamp, entries: [] });
                actualGroupIndex = this.groups.length - 1;
              } else {
                this.groups.splice(nextIndex, 0, { key: normalizedTimestamp, entries: [] });
                actualGroupIndex = nextIndex;
              }
            }

            const existingEntryIndex = this.groups[actualGroupIndex].entries.findIndex((entry) => entry.title === title && entry.project === project);
            if (existingEntryIndex < 0) this.groups[actualGroupIndex].entries.push({ title, project, hours });
            else this.groups[actualGroupIndex].entries[existingEntryIndex].hours += hours;
          }
        });
      });

      if (!this.initialized) this.initialized = true;
    },
  },
};
</script>

<style lang="stylus" scoped>
@require '../styles/breakpoints'
@require '../styles/colors'
@require '../styles/shadows'

.activity
  @media $desktop
    padding: 1rem !important

  .activity-graph
    margin-top: -1.5rem
    margin-bottom: 1.5rem
    border-top-left-radius: 0
    border-top-right-radius: 0
    padding-top: 3rem

  .card
    background-color: $bg
    color: $text
    padding: 1.5rem
    border-radius: 1.5rem
    box-shadow: $shadow
    position: relative
    overflow: hidden
    margin: 0 0.5rem

    @media $desktop
      margin: 0
      padding: 2rem
      overflow: visible

    @media $desktopLarge
      padding: 8rem

    .group
      margin-bottom: 1.5rem
      background-color: $bg

      @media $desktop
        margin-bottom: 2rem

      &:not(:last-child)
        border-bottom: 1px solid alpha($accent, 0.1)
        padding-bottom: 1rem

        @media $desktop
          padding-bottom: 2rem

      &.empty-state
        text-align: center
        margin-bottom: 0
        padding: 1.5rem

        p
          margin: 0

      h2
        font-weight: bold
        font-size: 1rem
        letter-spacing: 0.03em
        color: $accent
        margin-bottom: 1.5rem

        @media $desktop
          font-size: 1.5rem
          margin-bottom: 2.5rem

      .entry
        display: flex
        justify-content: space-between
        margin-bottom: 1rem

        .task
          margin-right: 1.5rem

          p
            margin: 0

          span
            font-size: 0.75rem
            text-transform: capitalize

  .load-more-button
    margin: 2rem auto 0 auto
</style>
